import { useEffect, useMemo, useState } from "react";
import ChatwootAudit from "@/pages/ChatwootAudit";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getChatwootSDK } from "@/integrations/chatwoot/sdk";
import ChatwootSetup from "./Chatwoot";
import { RefreshCw, QrCode } from "lucide-react";
import QRCode from "qrcode";
import { logActivity, logCrud } from "@/services/activities";
const ensureArray = (d: any) =>
  Array.isArray(d)
    ? d
    : Array.isArray(d?.payload)
    ? d.payload
    : Array.isArray(d?.data)
    ? d.data
    : Array.isArray(d?.webhooks)
    ? d.webhooks
    : Array.isArray(d?.resources)
    ? d.resources
    : Array.isArray(d?.inboxes)
    ? d.inboxes
    : Array.isArray(d?.agents)
    ? d.agents
    : Array.isArray(d?.teams)
    ? d.teams
    : Array.isArray(d?.labels)
    ? d.labels
    : Array.isArray(d?.users)
    ? d.users
    : [];
const filterByAccountId = (arr: any[], accountId?: number | null) => (Array.isArray(arr) ? arr.filter((x) => typeof x?.account_id === "undefined" || Number(x.account_id) === Number(accountId)) : []);
const computeStats = (arr: Array<{ value: any }>) => {
  const values = (arr || []).map((r) => Number(r.value)).filter((v) => !isNaN(v));
  const total = values.reduce((a, b) => a + b, 0);
  const count = values.length;
  const avg = count ? total / count : 0;
  const max = values.length ? Math.max(...values) : 0;
  return { total, avg, max, count };
};
const fmt = new Intl.NumberFormat("pt-BR");
const isValidUrl = (u: string) => {
  try { const x = new URL(String(u || "")); return !!x.protocol && !!x.host; } catch { return false; }
};
const isValidUrlOrEmpty = (u: string) => !u || isValidUrl(u);
const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || ""));
const isStrongPassword = (p: string) => {
  const s = String(p || "");
  return s.length >= 6 && /[A-Z]/.test(s) && /[0-9]/.test(s) && /[!@#$%^&*()_\-+=\[\]{}|\\:;"',.<>/?~]/.test(s);
};
const formatDuration = (s: number) => {
  const sec = Math.floor(s || 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const rem = sec % 60;
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const ss = String(rem).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};
const displayRole = (role?: string | null) => {
  switch (String(role || "").toLowerCase()) {
    case "administrator":
      return "Administrador";
    case "agent":
      return "Agente";
    default:
      return role ? String(role) : "—";
  }
};

const displayAvailability = (status?: string | null) => {
  switch (String(status || "").toLowerCase()) {
    case "available":
      return "Disponível";
    case "busy":
      return "Ocupado";
    case "offline":
      return "Offline";
    default:
      return status ? String(status) : "—";
  }
};
const yesNo = (v: any) => {
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  const s = String(v ?? "").toLowerCase();
  if (s === "true") return "Sim";
  if (s === "false") return "Não";
  return v ?? "—";
};
const displayRolePT = (role?: string | null) => {
  switch (String(role || "").toLowerCase()) {
    case "administrator":
      return "Administrador";
    case "agent":
      return "Atendente";
    default:
      return role ? String(role) : "—";
  }
};
const displayActivePT = (status: any) => {
  if (typeof status === "boolean") return status ? "Ativo" : "Inativo";
  const s = String(status ?? "").toLowerCase();
  if (s.includes("active")) return "Ativo";
  if (s.includes("inactive")) return "Inativo";
  return status ?? "—";
};
export function Sparkline({ data }: { data: Array<{ value: any }> }) {
  const values = (data || []).map((r) => Number(r.value)).filter((v) => !isNaN(v));
  const subset = values.slice(-24);
  const max = subset.length ? Math.max(...subset) : 0;
  return (
    <div className="flex items-end gap-1 h-16">
      {subset.map((v, i) => (
        <div key={i} className="w-2 bg-muted-foreground/40" style={{ height: `${max ? Math.max(4, Math.round((v / max) * 100)) : 4}%` }} />
      ))}
    </div>
  );
}

const CHATWOOT_BASE_URL = typeof window !== "undefined" && (window as any).__CHATWOOT_BASE_URL__
  ? (window as any).__CHATWOOT_BASE_URL__
  : import.meta.env.VITE_CHATWOOT_BASE_URL || "https://app.chatwoot.com";

type ChatwootConfig = { token: string | null; accountId: number | null };

function useChatwootConfig(enabled: boolean) {
  const { tenantId } = useTenant();
  return useQuery<ChatwootConfig>({
    queryKey: ["chatwoot-config", tenantId],
    enabled: enabled && !!tenantId,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    queryFn: async () => {
      const { data: authUser } = await supabase.auth.getUser();
      const userId = authUser?.user?.id;
      const { data: profile } = await supabase
        .from("profiles")
        .select("chatwoot_user_token")
        .eq("id", userId)
        .single();

      const { data: tenant } = await supabase
        .from("tenants" as any)
        .select("chatwoot_account_id")
        .eq("id", tenantId)
        .single();

      return {
        token: (profile as any)?.chatwoot_user_token || null,
        accountId: (tenant as any)?.chatwoot_account_id || null,
      };
    },
  });
}

export default function SettingsChatwoot() {
  const { tenantName } = useTenant();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(() => searchParams.get("tab") || "setup");
  const { data: cfg } = useChatwootConfig(true);

  const [metric, setMetric] = useState<string>("conversations_count");
  const [type, setType] = useState<string>("account");
  const [objectId, setObjectId] = useState<string>("");
  const [since, setSince] = useState<string>("");
  const [until, setUntil] = useState<string>("");
  const [periodPreset, setPeriodPreset] = useState<string>("semana_passada");
  const [selectedEntity, setSelectedEntity] = useState<string>(() => searchParams.get("sub") || "agents");

  useEffect(() => {
    const current = new URLSearchParams(searchParams);
    current.set("tab", activeTab);
    current.set("sub", selectedEntity);
    setSearchParams(current);
  }, [activeTab, selectedEntity]);

  const requiresId = false;
  const reportsEnabled = !!cfg?.token && !!cfg?.accountId && !!since && !!until && activeTab === "reports";

  const fetchReport = async (m: string, t: string, id?: string) => {
    const data = await sdk.appProxy<Array<{ value: string | number; timestamp: number }>>({
      path: `/api/v2/accounts/${cfg!.accountId}/reports`,
      method: "GET",
      token: cfg!.token as string,
      query: {
        metric: m,
        type: t,
        id: t !== "account" && id ? id : undefined,
        since: since ? Math.floor(new Date(since).getTime() / 1000) : undefined,
        until: until ? Math.floor(new Date(until).getTime() / 1000) : undefined,
      },
    });
    return data;
  };

  const [convType, setConvType] = useState<string>("account");
  const [convId, setConvId] = useState<string>("");
  const [inType, setInType] = useState<string>("account");
  const [inId, setInId] = useState<string>("");
  const [outType, setOutType] = useState<string>("account");
  const [outId, setOutId] = useState<string>("");
  const [afrType, setAfrType] = useState<string>("account");
  const [afrId, setAfrId] = useState<string>("");
  const [arType, setArType] = useState<string>("account");
  const [arId, setArId] = useState<string>("");
  const [resType, setResType] = useState<string>("account");
  const [resId, setResId] = useState<string>("");

  const conversationsCount = useQuery({
    queryKey: ["chatwoot-reports", cfg?.accountId, "conversations_count", convType, convId, since, until, cfg?.token],
    enabled: reportsEnabled && (convType === "account" || !!convId),
    queryFn: () => fetchReport("conversations_count", convType, convId),
  });
  const incomingMessagesCount = useQuery({
    queryKey: ["chatwoot-reports", cfg?.accountId, "incoming_messages_count", inType, inId, since, until, cfg?.token],
    enabled: reportsEnabled && (inType === "account" || !!inId),
    queryFn: () => fetchReport("incoming_messages_count", inType, inId),
  });
  const outgoingMessagesCount = useQuery({
    queryKey: ["chatwoot-reports", cfg?.accountId, "outgoing_messages_count", outType, outId, since, until, cfg?.token],
    enabled: reportsEnabled && (outType === "account" || !!outId),
    queryFn: () => fetchReport("outgoing_messages_count", outType, outId),
  });
  const avgFirstResponseTime = useQuery({
    queryKey: ["chatwoot-reports", cfg?.accountId, "avg_first_response_time", afrType, afrId, since, until, cfg?.token],
    enabled: reportsEnabled && (afrType === "account" || !!afrId),
    queryFn: () => fetchReport("avg_first_response_time", afrType, afrId),
  });
  const avgResolutionTime = useQuery({
    queryKey: ["chatwoot-reports", cfg?.accountId, "avg_resolution_time", arType, arId, since, until, cfg?.token],
    enabled: reportsEnabled && (arType === "account" || !!arId),
    queryFn: () => fetchReport("avg_resolution_time", arType, arId),
  });
  const resolutionsCount = useQuery({
    queryKey: ["chatwoot-reports", cfg?.accountId, "resolutions_count", resType, resId, since, until, cfg?.token],
    enabled: reportsEnabled && (resType === "account" || !!resId),
    queryFn: () => fetchReport("resolutions_count", resType, resId),
  });

  const computeRange = (preset: string) => {
    const today = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    const startOfWeek = (d: Date) => { const day = d.getDay(); const diff = (day + 6) % 7; const s = new Date(d); s.setDate(d.getDate() - diff); return startOfDay(s); };
    const endOfWeek = (d: Date) => { const s = startOfWeek(d); const e = new Date(s); e.setDate(s.getDate() + 6); return endOfDay(e); };
    const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
    const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    const startOfYear = (d: Date) => new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
    const endOfYear = (d: Date) => new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);
    if (preset === "hoje") return { s: startOfDay(today), u: endOfDay(today) };
    if (preset === "ontem") { const y = new Date(today); y.setDate(today.getDate() - 1); return { s: startOfDay(y), u: endOfDay(y) }; }
    if (preset === "antes_de_ontem") { const y = new Date(today); y.setDate(today.getDate() - 2); return { s: startOfDay(y), u: endOfDay(y) }; }
    if (preset === "essa_semana") return { s: startOfWeek(today), u: endOfDay(today) };
    if (preset === "semana_passada") { const d = new Date(today); d.setDate(today.getDate() - 7); return { s: startOfWeek(d), u: endOfWeek(d) }; }
    if (preset === "semana_retrasada") { const d = new Date(today); d.setDate(today.getDate() - 14); return { s: startOfWeek(d), u: endOfWeek(d) }; }
    if (preset === "este_mes") return { s: startOfMonth(today), u: endOfDay(today) };
    if (preset === "mes_passado") { const d = new Date(today.getFullYear(), today.getMonth() - 1, 1); return { s: startOfMonth(d), u: endOfMonth(d) }; }
    if (preset === "mes_retrasado") { const d = new Date(today.getFullYear(), today.getMonth() - 2, 1); return { s: startOfMonth(d), u: endOfMonth(d) }; }
    if (preset === "este_ano") return { s: startOfYear(today), u: endOfDay(today) };
    if (preset === "ano_passado") { const d = new Date(today.getFullYear() - 1, 0, 1); return { s: startOfYear(d), u: endOfYear(d) }; }
    if (preset === "ano_retrasado") { const d = new Date(today.getFullYear() - 2, 0, 1); return { s: startOfYear(d), u: endOfYear(d) }; }
    if (preset === "ultimas_duas_semanas") { const d = new Date(today); d.setDate(today.getDate() - 14); return { s: startOfDay(d), u: endOfDay(today) }; }
    if (preset === "ultimas_tres_semanas") { const d = new Date(today); d.setDate(today.getDate() - 21); return { s: startOfDay(d), u: endOfDay(today) }; }
    if (preset === "ultimos_dois_meses") { const d = new Date(today.getFullYear(), today.getMonth() - 2, today.getDate()); return { s: startOfDay(d), u: endOfDay(today) }; }
    if (preset === "ultimos_tres_meses") { const d = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate()); return { s: startOfDay(d), u: endOfDay(today) }; }
    return { s: startOfWeek(today), u: endOfWeek(today) };
  };

  useEffect(() => {
    const r = computeRange(periodPreset);
    setSince(new Date(r.s).toISOString().slice(0, 16));
    setUntil(new Date(r.u).toISOString().slice(0, 16));
  }, [periodPreset]);

  const webhooksEnabled = !!cfg?.token && !!cfg?.accountId;

  const { data: webhooks, isLoading: webhooksLoading } = useQuery({
    queryKey: ["chatwoot-webhooks", cfg?.accountId, cfg?.token],
    enabled: webhooksEnabled,
    queryFn: async () => {
      const data = await sdk.appProxy<any>({
        path: `/api/v1/accounts/${cfg!.accountId}/webhooks`,
        method: "GET",
        token: cfg!.token as string,
      });
      return ensureArray(data);
    },
  });

  const createWebhookMutation = useMutation({
    mutationFn: async (payload: { url: string; subscriptions: string[] }) => {
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/webhooks`,
        method: "POST",
        token: cfg!.token as string,
        body: payload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatwoot-webhooks", cfg?.accountId, cfg?.token] });
      toast.success("Webhook criado");
      void logCrud({ entitySlug: "entity:api_keys", operationSlug: "create", screenSlug: "configuracoes/chatwoot" });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao criar webhook"),
  });

  const updateWebhookMutation = useMutation({
    mutationFn: async (payload: { id: number; url: string; subscriptions: string[] }) => {
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/webhooks/${payload.id}`,
        method: "PATCH",
        token: cfg!.token as string,
        body: { url: payload.url, subscriptions: payload.subscriptions },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatwoot-webhooks", cfg?.accountId, cfg?.token] });
      toast.success("Webhook atualizado");
      void logCrud({ entitySlug: "entity:api_keys", operationSlug: "update", screenSlug: "configuracoes/chatwoot" });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao atualizar webhook"),
  });

  const [whUrl, setWhUrl] = useState<string>("");
  const [whSubsSelected, setWhSubsSelected] = useState<Record<string, boolean>>({ conversation_created: true });
  const [selectedInbox, setSelectedInbox] = useState<number | null>(null);
  const [selectedAgentBot, setSelectedAgentBot] = useState<number | null>(null);
  const [inboxBotMap, setInboxBotMap] = useState<Record<number, { id: number; name: string } | null>>({});
  const [accountEdit, setAccountEdit] = useState<{ name?: string; support_email?: string; locale?: string; domain?: string }>({});
  const [inboxMembersLoading, setInboxMembersLoading] = useState<boolean>(false);
  const [newPlatformUser, setNewPlatformUser] = useState<{ name: string; email: string; password: string; display_name: string }>({ name: "", email: "", password: "", display_name: "" });
  const [linkRole, setLinkRole] = useState<string>("administrator");
  const [passwordError, setPasswordError] = useState<string>("");
  const sdk = getChatwootSDK();
  const { data: accountUsers, isLoading: accountUsersLoading } = useQuery({
    queryKey: ["chatwoot-account-users", cfg?.accountId],
    enabled: !!cfg?.accountId && selectedEntity === "users",
    retry: 0,
    queryFn: async () => {
      try {
        if (cfg?.token) {
          const data = await sdk.appProxy<any>({
            path: `/api/v1/accounts/${cfg!.accountId}/agents`,
            method: "GET",
            token: cfg!.token as string,
          });
          const arr = ensureArray(data).map((u: any) => ({ account_id: Number(u?.account_id ?? cfg!.accountId), user_id: Number(u?.id), role: String(u?.role ?? "agent") }));
          return arr;
        }
        const res = await sdk.listAccountUsers(cfg!.accountId!);
        return res.users;
      } catch (e: any) {
        try {
          const res = await sdk.listAccountUsers(cfg!.accountId!);
          return res.users;
        } catch (err: any) {
          toast.error(err?.message || e?.message || "Erro ao listar usuários da conta");
          return [] as any[];
        }
      }
    },
  });
  const createPlatformUserMutation = useMutation({
    mutationFn: async () => {
      const res = await sdk.createUser({
        name: newPlatformUser.name,
        email: newPlatformUser.email,
        password: newPlatformUser.password,
        display_name: newPlatformUser.display_name,
      });
      return res;
    },
    onSuccess: (data: any) => {
      toast.success("Usuário criado no Chatwoot");
      const userId = data?.chatwoot_user_id;
      if (userId) {
        linkAccountUserMutation.mutate({ user_id: userId, role: linkRole as any });
      }
      setPasswordError("");
    },
    onError: (e: any) => {
      const msg = String(e?.message || e || "");
      const m = msg.includes("Password") ? msg.replace(/^.*422\s*/, "").trim() : msg;
      if (msg.toLowerCase().includes("password")) setPasswordError(m);
      toast.error(msg || "Erro ao criar usuário");
    },
  });
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const linkAccountUserMutation = useMutation({
    mutationFn: async ({ user_id, role }: { user_id: number; role: "administrator" | "agent" }) => {
      const res = await sdk.linkAccountUser({ user_id, role });
      return res;
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ["chatwoot-account-users", cfg?.accountId] });
      const prev = queryClient.getQueryData<any>(["chatwoot-account-users", cfg?.accountId]);
      if (prev && Array.isArray(prev)) {
        const next = prev.map((u: any) => (u.user_id === vars.user_id ? { ...u, role: vars.role } : u));
        queryClient.setQueryData(["chatwoot-account-users", cfg?.accountId], next);
      }
      return { prev } as any;
    },
    onSuccess: () => {
      toast.success("Usuário vinculado/atualizado no Account");
      void logActivity({ title: "Chatwoot: usuário vinculado/atualizado", categorySlug: "category:chatwoot", screenSlug: "configuracoes/chatwoot", operationSlug: "operation:update" });
      setNewPlatformUser({ name: "", email: "", password: "", display_name: "" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-users", cfg?.accountId] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao vincular/atualizar usuário"),
  });
  const unlinkAccountUserMutation = useMutation({
    mutationFn: async ({ user_id }: { user_id: number }) => {
      const res = await sdk.unlinkAccountUser(user_id);
      return res;
    },
    onSuccess: () => {
      toast.success("Usuário removido do Account");
      void logActivity({ title: "Chatwoot: usuário removido do account", categorySlug: "category:chatwoot", screenSlug: "configuracoes/chatwoot", operationSlug: "operation:delete" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-users", cfg?.accountId] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao remover usuário"),
  });

  const saveUserMutation = useMutation({
    mutationFn: async () => {
      if (!editingUserId) throw new Error("Selecione um usuário");
      await sdk.appProxy({
        path: `/api/v1/users/${editingUserId}`,
        method: "PATCH",
        token: cfg!.token as string,
        body: { name: newPlatformUser.name, display_name: newPlatformUser.display_name },
      });
      await linkAccountUserMutation.mutateAsync({ user_id: editingUserId, role: linkRole as any });
    },
    onSuccess: () => {
      toast.success("Usuário atualizado");
      setEditingUserId(null);
      setNewPlatformUser({ name: "", email: "", password: "", display_name: "" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-users", cfg?.accountId] });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-agents", cfg?.accountId, cfg?.token] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao atualizar usuário"),
  });

  const commonEnabled = !!cfg?.token && !!cfg?.accountId;

  const { data: agents, isLoading: agentsLoading } = useQuery({
    queryKey: ["chatwoot-agents", cfg?.accountId, cfg?.token],
    enabled: commonEnabled && (selectedEntity === "agents" || selectedEntity === "users" || activeTab === "bots" || activeTab === "integration" || activeTab === "reports"),
    queryFn: async () => {
      const data = await sdk.appProxy<any>({
        path: `/api/v1/accounts/${cfg!.accountId}/agents`,
        method: "GET",
        token: cfg!.token as string,
      });
      return filterByAccountId(ensureArray(data), cfg?.accountId);
    },
  });
  const agentById = useMemo(() => {
    const map: Record<number, any> = {};
    (Array.isArray(agents) ? agents : []).forEach((a: any) => { map[a.id] = a; });
    return map;
  }, [agents]);
  const combinedAccountAgentsRows = useMemo(() => {
    const rows: Array<{ user_id: number; role: string; name: string; email: string; availability_status: string }> = [];
    const seen = new Set<number>();
    (Array.isArray(accountUsers) ? accountUsers : []).forEach((u: any) => {
      const a = agentById[u.user_id] || {};
      rows.push({
        user_id: u.user_id,
        role: u.role,
        name: a.name || "",
        email: a.email || "",
        availability_status: a.availability_status || "offline",
      });
      seen.add(u.user_id);
    });
    (Array.isArray(agents) ? agents : []).forEach((a: any) => {
      if (!seen.has(a.id)) {
        rows.push({
          user_id: a.id,
          role: a.role || "agent",
          name: a.name || "",
          email: a.email || "",
          availability_status: a.availability_status || "offline",
        });
      }
    });
    return rows;
  }, [accountUsers, agents, agentById]);
  const [newAgent, setNewAgent] = useState<{ name: string; email: string; role: string; availability_status?: string; auto_offline?: boolean }>({ name: "", email: "", role: "agent", availability_status: "available", auto_offline: true });
  const [editingAgentId, setEditingAgentId] = useState<number | null>(null);
  const createAgentMutation = useMutation({
    mutationFn: async () => {
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/agents`,
        method: "POST",
        token: cfg!.token as string,
        body: newAgent,
      });
    },
    onSuccess: () => {
      toast.success("Agente criado");
      void logActivity({ title: "Chatwoot: agente criado", categorySlug: "category:chatwoot", screenSlug: "configuracoes/chatwoot", operationSlug: "operation:create" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-agents", cfg?.accountId, cfg?.token] });
      setNewAgent({ name: "", email: "", role: "agent", availability_status: "available", auto_offline: true });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao criar agente"),
  });
  const saveAgentRoleMutation = useMutation({
    mutationFn: async () => {
      if (!editingAgentId) throw new Error("Selecione um agente");
      const role = (newAgent.role as any) || "agent";
      await linkAccountUserMutation.mutateAsync({ user_id: editingAgentId, role });
    },
    onSuccess: () => {
      toast.success("Agente atualizado");
      void logActivity({ title: "Chatwoot: agente atualizado", categorySlug: "category:chatwoot", screenSlug: "configuracoes/chatwoot", operationSlug: "operation:update" });
      setEditingAgentId(null);
      queryClient.invalidateQueries({ queryKey: ["chatwoot-agents", cfg?.accountId, cfg?.token] });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-users", cfg?.accountId] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao atualizar agente"),
  });
  const deleteAgentMutation = useMutation({
    mutationFn: async (id: number) => {
      if (!cfg?.token || !cfg?.accountId) throw new Error("Token/Account ausente");
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/agents/${id}`,
        method: "DELETE",
        token: cfg!.token as string,
      });
    },
    onSuccess: () => {
      toast.success("Agente removido");
      void logActivity({ title: "Chatwoot: agente removido", categorySlug: "category:chatwoot", screenSlug: "configuracoes/chatwoot", operationSlug: "operation:delete" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-agents", cfg?.accountId, cfg?.token] });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-users", cfg?.accountId] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao remover agente"),
  });
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const updateAgentAvailabilityMutation = useMutation({
    mutationFn: async ({ id, availability_status }: { id: number; availability_status: string }) => {
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/agents/${id}`,
        method: "PATCH",
        token: cfg!.token as string,
        body: { availability_status },
      });
    },
    onSuccess: () => {
      toast.success("Disponibilidade atualizada");
      void logActivity({ title: "Chatwoot: disponibilidade atualizada", categorySlug: "category:chatwoot", screenSlug: "configuracoes/chatwoot", operationSlug: "operation:update" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-agents", cfg?.accountId, cfg?.token] });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-users", cfg?.accountId] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao atualizar disponibilidade"),
  });

  const updateAgentRoleMutation = useMutation({
    mutationFn: async ({ id, role }: { id: number; role: "agent" | "administrator" }) => {
      if (!cfg?.token || !cfg?.accountId) throw new Error("Token/Account ausente");
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/agents/${id}`,
        method: "PATCH",
        token: cfg!.token as string,
        body: { role },
      });
    },
    onSuccess: () => {
      toast.success("Papel atualizado");
      void logActivity({ title: "Chatwoot: papel atualizado", categorySlug: "category:chatwoot", screenSlug: "configuracoes/chatwoot", operationSlug: "operation:update" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-agents", cfg?.accountId, cfg?.token] });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-users", cfg?.accountId] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao atualizar papel"),
  });
  const [evoInstance, setEvoInstance] = useState<string>("");
  const [evoQrDataUrl, setEvoQrDataUrl] = useState<string>("");
  const [evoPairingCode, setEvoPairingCode] = useState<string>("");
  const [evoLoading, setEvoLoading] = useState<boolean>(false);
  const EVO_BASE = import.meta.env.VITE_EVOLUTION_BASE_URL || "";
  const EVO_KEY = import.meta.env.VITE_EVOLUTION_API_KEY || "";
  const fetchEvolutionQr = async () => {
    try {
      setEvoLoading(true);
      setEvoQrDataUrl("");
      setEvoPairingCode("");
      if (!EVO_BASE || !EVO_KEY) throw new Error("Configuração da Evolution API ausente");
      if (!evoInstance) throw new Error("Informe a instância Evolution");
      const res = await fetch(`${EVO_BASE.replace(/\/$/, "")}/instance/connect/${encodeURIComponent(evoInstance)}`, {
        headers: { apikey: EVO_KEY },
      });
      if (!res.ok) throw new Error(`Falha ao obter QR (${res.status})`);
      const data = await res.json();
      const code = data?.code || data?.qrCode || data?.qrcode || "";
      const pairing = data?.pairingCode || data?.pairing || "";
      if (!code && !pairing) throw new Error("Resposta sem código de pareamento");
      setEvoPairingCode(String(pairing || ""));
      if (code) {
        const url = await QRCode.toDataURL(String(code));
        setEvoQrDataUrl(url);
      }
      toast.success("QR/pareamento gerado");
    } catch (e: any) {
      toast.error(e.message || "Erro ao gerar QR");
    } finally {
      setEvoLoading(false);
    }
  };

  const { data: teams, isLoading: teamsLoading } = useQuery({
    queryKey: ["chatwoot-teams", cfg?.accountId, cfg?.token],
    enabled: commonEnabled && (selectedEntity === "teams" || activeTab === "reports"),
    queryFn: async () => {
      const data = await sdk.appProxy<any>({
        path: `/api/v1/accounts/${cfg!.accountId}/teams`,
        method: "GET",
        token: cfg!.token as string,
      });
      return filterByAccountId(ensureArray(data), cfg?.accountId);
    },
  });
  const [teamMembersMap, setTeamMembersMap] = useState<Record<number, number[]>>({});
  useEffect(() => {
    const loadMembers = async () => {
      if (!commonEnabled || selectedEntity !== "teams") return;
      if (!Array.isArray(teams) || teams.length === 0) return;
      try {
        const entries = await Promise.all(
          teams.map(async (t: any) => {
            const data = await sdk.appProxy<any>({
              path: `/api/v1/accounts/${cfg!.accountId}/teams/${t.id}/team_members`,
              method: "GET",
              token: cfg!.token as string,
            });
            const ids = ensureArray(data).map((m: any) => Number(m?.id || m?.user_id)).filter((x: any) => !isNaN(x));
            return [t.id, ids] as const;
          })
        );
        const map: Record<number, number[]> = {};
        for (const [id, ids] of entries) map[id] = ids;
        setTeamMembersMap(map);
      } catch {}
    };
    loadMembers();
  }, [teams, selectedEntity, cfg?.accountId, cfg?.token, commonEnabled]);
  const [newTeam, setNewTeam] = useState<{ name: string; description?: string }>({ name: "", description: "" });
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
  const [selectedAgentIds, setSelectedAgentIds] = useState<Record<number, boolean>>({});
  const createTeamMutation = useMutation({
    mutationFn: async () => {
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/teams`,
        method: "POST",
        token: cfg!.token as string,
        body: { name: newTeam.name, description: newTeam.description || undefined },
      });
    },
    onSuccess: () => {
      toast.success("Team criado");
      void logActivity({ title: "Chatwoot: team criado", categorySlug: "category:chatwoot", screenSlug: "configuracoes/chatwoot", operationSlug: "operation:create" });
      setNewTeam({ name: "", description: "" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-teams", cfg?.accountId, cfg?.token] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao criar team"),
  });
  const updateTeamMutation = useMutation({
    mutationFn: async () => {
      if (!editingTeamId) throw new Error("Selecione um team");
      const ids = Object.keys(selectedAgentIds)
        .map((k) => Number(k))
        .filter((id) => selectedAgentIds[id]);
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/teams/${editingTeamId}/team_members`,
        method: "PATCH",
        token: cfg!.token as string,
        body: { user_ids: ids },
      });
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/teams/${editingTeamId}`,
        method: "PATCH",
        token: cfg!.token as string,
        body: { name: newTeam.name || undefined, description: newTeam.description || undefined },
      });
    },
    onSuccess: () => {
      toast.success("Team atualizado");
      void logActivity({ title: "Chatwoot: team atualizado", categorySlug: "category:chatwoot", screenSlug: "configuracoes/chatwoot", operationSlug: "operation:update" });
      setEditingTeamId(null);
      setNewTeam({ name: "", description: "" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-teams", cfg?.accountId, cfg?.token] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao atualizar team"),
  });
  const deleteTeamMutation = useMutation({
    mutationFn: async (id: number) => {
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/teams/${id}`,
        method: "DELETE",
        token: cfg!.token as string,
      });
    },
    onSuccess: () => {
      toast.success("Team removido");
      void logActivity({ title: "Chatwoot: team removido", categorySlug: "category:chatwoot", screenSlug: "configuracoes/chatwoot", operationSlug: "operation:delete" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-teams", cfg?.accountId, cfg?.token] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao remover team"),
  });

  useEffect(() => {
    setSelectedTeam(editingTeamId);
  }, [editingTeamId]);

  const { data: teamMembers } = useQuery({
    queryKey: ["chatwoot-team-members", cfg?.accountId, editingTeamId, cfg?.token],
    enabled: commonEnabled && selectedEntity === "teams" && !!editingTeamId,
    queryFn: async () => {
      const data = await sdk.appProxy<any>({
        path: `/api/v1/accounts/${cfg!.accountId}/teams/${editingTeamId}/team_members`,
        method: "GET",
        token: cfg!.token as string,
      });
      return ensureArray(data);
    },
  });

  useEffect(() => {
    if (Array.isArray(teamMembers)) {
      const preset: Record<number, boolean> = {};
      (teamMembers || []).forEach((m: any) => {
        const id = Number(m?.id || m?.user_id);
        if (!isNaN(id)) preset[id] = true;
      });
      setSelectedAgentIds(preset);
    }
  }, [teamMembers]);

  const addTeamMembersMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      if (!selectedTeam) throw new Error("Selecione o team");
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/teams/${selectedTeam}/team_members`,
        method: "POST",
        token: cfg!.token as string,
        body: { user_ids: ids },
      });
    },
    onSuccess: () => {
      toast.success("Agentes adicionados ao team");
      queryClient.invalidateQueries({ queryKey: ["chatwoot-team-members", cfg?.accountId, selectedTeam, cfg?.token] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao adicionar"),
  });

  const { data: inboxes, isLoading: inboxesLoading } = useQuery({
    queryKey: ["chatwoot-inboxes", cfg?.accountId, cfg?.token],
    enabled: commonEnabled && (selectedEntity === "inboxes" || activeTab === "bots" || activeTab === "integration" || activeTab === "reports"),
    queryFn: async () => {
      const data = await sdk.appProxy<any>({
        path: `/api/v1/accounts/${cfg!.accountId}/inboxes`,
        method: "GET",
        token: cfg!.token as string,
      });
      return filterByAccountId(ensureArray(data), cfg?.accountId);
    },
  });

  useEffect(() => {
    if ((activeTab === "bots" || activeTab === "integration") && commonEnabled) {
      queryClient.invalidateQueries({ queryKey: ["chatwoot-inboxes", cfg?.accountId, cfg?.token] });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-agent-bots", cfg?.accountId, cfg?.token] });
    }
  }, [activeTab, commonEnabled, cfg?.accountId, cfg?.token]);

  const { data: account, isLoading: accountLoading } = useQuery({
    queryKey: ["chatwoot-account", cfg?.accountId, cfg?.token],
    enabled: commonEnabled && selectedEntity === "account",
    queryFn: async () => {
      try {
        const data = await sdk.appProxy<{ id: number; name: string; support_email?: string; locale?: string; domain?: string }>({
          path: `/api/v1/accounts/${cfg!.accountId}`,
          method: "GET",
          token: cfg!.token as string,
        });
        return data as any;
      } catch {
        const prof: any = await sdk.appProxy<any>({
          path: `/api/v1/profile`,
          method: "GET",
          token: cfg!.token as string,
        });
        const accId = Number(prof?.account?.id || prof?.account_id);
        if (accId && accId === Number(cfg!.accountId)) {
          return {
            id: accId,
            name: prof?.account?.name || undefined,
            support_email: prof?.account?.support_email || undefined,
            locale: prof?.account?.locale || undefined,
            domain: prof?.account?.domain || undefined,
          } as any;
        }
        throw new Error("Conta não encontrada para o tenant atual");
      }
    },
  });

  useEffect(() => {
    if (selectedEntity === "account" && account) {
      setAccountEdit({
        name: (account as any)?.name || "",
        support_email: (account as any)?.support_email || "",
        locale: (account as any)?.locale || "pt_BR",
        domain: (account as any)?.domain || "",
      });
    }
  }, [selectedEntity, account]);

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["chatwoot-profile", cfg?.token],
    enabled: !!cfg?.token && selectedEntity === "profiles",
    queryFn: async () => {
      const data = await sdk.appProxy<any>({
        path: `/api/v1/profile`,
        method: "GET",
        token: cfg!.token as string,
      });
      return data;
    },
  });

  const { data: accountAgentBots, isLoading: accountAgentBotsLoading } = useQuery({
    queryKey: ["chatwoot-account-agent-bots", cfg?.accountId, cfg?.token],
    enabled: commonEnabled && (selectedEntity === "agentbots" || activeTab === "bots" || activeTab === "integration"),
    queryFn: async () => {
      const data = await sdk.appProxy<any>({
        path: `/api/v1/accounts/${cfg!.accountId}/agent_bots`,
        method: "GET",
        token: cfg!.token as string,
      });
      return filterByAccountId(ensureArray(data), cfg?.accountId);
    },
  });
  const [editingBotId, setEditingBotId] = useState<number | null>(null);
  const [newBot, setNewBot] = useState<{ name: string; outgoing_url: string }>({ name: "", outgoing_url: "" });
  const createBotMutation = useMutation({
    mutationFn: async () => {
      if (!newBot.name) throw new Error("Informe o nome do bot");
      if (!isValidUrlOrEmpty(newBot.outgoing_url)) throw new Error("Webhook inválido");
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/agent_bots`,
        method: "POST",
        token: cfg!.token as string,
        body: { name: newBot.name, outgoing_url: newBot.outgoing_url || undefined },
      });
    },
    onSuccess: () => {
      toast.success("Bot criado");
      setNewBot({ name: "", outgoing_url: "" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-agent-bots", cfg?.accountId, cfg?.token] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao criar bot"),
  });
  const updateBotMutation = useMutation({
    mutationFn: async () => {
      if (!editingBotId) throw new Error("Selecione um bot");
      if (!newBot.name) throw new Error("Informe o nome do bot");
      if (!isValidUrlOrEmpty(newBot.outgoing_url)) throw new Error("Webhook inválido");
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/agent_bots/${editingBotId}`,
        method: "PATCH",
        token: cfg!.token as string,
        body: { name: newBot.name, outgoing_url: newBot.outgoing_url || null },
      });
    },
    onSuccess: () => {
      toast.success("Bot atualizado");
      setEditingBotId(null);
      setNewBot({ name: "", outgoing_url: "" });
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-agent-bots", cfg?.accountId, cfg?.token] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao atualizar bot"),
  });
  const deleteBotMutation = useMutation({
    mutationFn: async (id: number) => {
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/agent_bots/${id}`,
        method: "DELETE",
        token: cfg!.token as string,
      });
    },
    onSuccess: () => {
      toast.success("Bot excluído");
      queryClient.invalidateQueries({ queryKey: ["chatwoot-account-agent-bots", cfg?.accountId, cfg?.token] });
    },
    onError: (e: any) => toast.error(e.message || "Erro ao excluir bot"),
  });

  const associateAgentBotMutation = useMutation({
    mutationFn: async (payload: { inboxId: number; agentBotId: number | null }) => {
      await sdk.appProxy({
        path: `/api/v1/accounts/${cfg!.accountId}/inboxes/${payload.inboxId}/set_agent_bot`,
        method: "POST",
        token: cfg!.token as string,
        body: { agent_bot: payload.agentBotId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatwoot-inboxes", cfg?.accountId, cfg?.token] });
      toast.success("Associação atualizada");
    },
    onError: (e: any) => toast.error(e.message || "Erro na associação"),
  });

  const { data: labels, isLoading: labelsLoading } = useQuery({
    queryKey: ["chatwoot-labels", cfg?.accountId, cfg?.token],
    enabled: commonEnabled && activeTab === "reports",
    queryFn: async () => {
      const data = await sdk.appProxy<any>({
        path: `/api/v1/accounts/${cfg!.accountId}/labels`,
        method: "GET",
        token: cfg!.token as string,
      });
      return filterByAccountId(ensureArray(data), cfg?.accountId);
    },
  });

  return (
    <div className="space-y-6" data-cy="settings-chatwoot-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configurações · Chatwoot</h1>
        <p className="text-muted-foreground">Gerencie a integração avançada do Chatwoot</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid w-full md:grid-cols-6">
        <TabsTrigger value="setup">Configuração</TabsTrigger>
        <TabsTrigger value="records">Registros</TabsTrigger>
        <TabsTrigger value="integration">Integração</TabsTrigger>
        <TabsTrigger value="webhooks">Webhook</TabsTrigger>
        <TabsTrigger value="bots">Bots</TabsTrigger>
        <TabsTrigger value="audit">Auditoria</TabsTrigger>
      </TabsList>

        <TabsContent value="setup">
          <ChatwootSetup />
        </TabsContent>

        <TabsContent value="reports">
          <Card>
            <CardHeader>
              <CardTitle>Relatórios</CardTitle>
              <CardDescription>Dados de {tenantName || "sua conta"} via Chatwoot</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!cfg?.token || !cfg?.accountId ? (
                <Badge variant="destructive">Token ou Account ID ausente</Badge>
              ) : (
                <div className="space-y-3">
                  <div className="grid gap-3 md:grid-cols-3">
                    <div>
                      <label className="text-sm">Métrica</label>
                      <Select value={metric} onValueChange={setMetric}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="conversations_count">conversations_count</SelectItem>
                          <SelectItem value="incoming_messages_count">incoming_messages_count</SelectItem>
                          <SelectItem value="outgoing_messages_count">outgoing_messages_count</SelectItem>
                          <SelectItem value="avg_first_response_time">avg_first_response_time</SelectItem>
                          <SelectItem value="avg_resolution_time">avg_resolution_time</SelectItem>
                          <SelectItem value="resolutions_count">resolutions_count</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm">Tipo</label>
                      <Select value={type} onValueChange={setType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="account">account</SelectItem>
                          <SelectItem value="agent">agent</SelectItem>
                          <SelectItem value="inbox">inbox</SelectItem>
                          <SelectItem value="label">label</SelectItem>
                          <SelectItem value="team">team</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      {type === "agent" && (
                        <>
                          <label className="text-sm">Agente</label>
                          <Select value={objectId} onValueChange={setObjectId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o agente" />
                            </SelectTrigger>
                            <SelectContent>
                              {(agents || []).map((a) => (
                                <SelectItem key={a.id} value={String(a.id)}>{`${a.id} · ${a.name}`}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          </>
                      )}
                      {type === "inbox" && (
                        <>
                          <label className="text-sm">Inbox</label>
                          <Select value={objectId} onValueChange={setObjectId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a inbox" />
                            </SelectTrigger>
                            <SelectContent>
                              {(inboxes || []).map((i) => (
                                <SelectItem key={i.id} value={String(i.id)}>{`${i.id} · ${i.name}`}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          </>
                      )}
                      {type === "team" && (
                        <>
                          <label className="text-sm">Team</label>
                          <Select value={objectId} onValueChange={setObjectId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o team" />
                            </SelectTrigger>
                            <SelectContent>
                              {(teams || []).map((t) => (
                                <SelectItem key={t.id} value={String(t.id)}>{`${t.id} · ${t.name}`}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </>
                      )}
                      {type === "label" && (
                        <>
                          <label className="text-sm">Label</label>
                          <Select value={objectId} onValueChange={setObjectId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o label" />
                            </SelectTrigger>
                            <SelectContent>
                              {(labels || []).map((l) => (
                                <SelectItem key={l.id} value={String(l.id)}>{`${l.id} · ${l.name || l.title || "label"}`}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </>
                      )}
                      {type === "account" && (
                        <>
                          <label className="text-sm">ID (não aplicável)</label>
                          <Input value={objectId} onChange={(e) => setObjectId(e.target.value)} placeholder="" disabled />
                        </>
                      )}
                    </div>
                  </div>
                  {requiresId && !objectId && (
                    <div className="text-sm">
                      <Badge variant="secondary">Informe o ID da entidade selecionada para gerar o relatório</Badge>
                    </div>
                  )}
                  <div className="grid gap-3 md:grid-cols-3">
                    <div>
                      <label className="text-sm">Desde</label>
                      <Input type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} />
                    </div>
                    <div>
                      <label className="text-sm">Até</label>
                      <Input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="w-full">
                        <label className="text-sm">Período</label>
                        <Select value={periodPreset} onValueChange={setPeriodPreset}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o período" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hoje">hoje</SelectItem>
                            <SelectItem value="ontem">ontem</SelectItem>
                            <SelectItem value="antes_de_ontem">antes de ontem</SelectItem>
                            <SelectItem value="essa_semana">essa semana</SelectItem>
                            <SelectItem value="semana_passada">semana passada</SelectItem>
                            <SelectItem value="semana_retrasada">semana retrasada</SelectItem>
                            <SelectItem value="este_mes">este mês</SelectItem>
                            <SelectItem value="mes_passado">mês passado</SelectItem>
                            <SelectItem value="mes_retrasado">mês retrasado</SelectItem>
                            <SelectItem value="este_ano">este ano</SelectItem>
                            <SelectItem value="ano_passado">ano passado</SelectItem>
                            <SelectItem value="ano_retrasado">ano retrasado</SelectItem>
                            <SelectItem value="ultimas_duas_semanas">últimas duas semanas</SelectItem>
                            <SelectItem value="ultimas_tres_semanas">últimas três semanas</SelectItem>
                            <SelectItem value="ultimos_dois_meses">últimos dois meses</SelectItem>
                            <SelectItem value="ultimos_tres_meses">últimos três meses</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ["chatwoot-reports"] })}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <Card>
                      <CardHeader><CardTitle>Conversations Count</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid gap-3 md:grid-cols-4 mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Total</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(conversationsCount.data || []).total)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Média</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(conversationsCount.data || []).avg)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Pico</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(conversationsCount.data || []).max)}</p>
                          </div>
                          <div>
                            <Sparkline data={conversationsCount.data || []} />
                          </div>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2 mb-3">
                          <div>
                            <label className="text-sm">Tipo</label>
                            <Select value={convType} onValueChange={(v) => { setConvType(v); setConvId(""); }}>
                              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="account">account</SelectItem>
                                <SelectItem value="agent">agent</SelectItem>
                                <SelectItem value="inbox">inbox</SelectItem>
                                <SelectItem value="label">label</SelectItem>
                                <SelectItem value="team">team</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {convType !== "account" && (
                            <div>
                              <label className="text-sm">Entidade</label>
                              <Select value={convId} onValueChange={setConvId}>
                                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent>
                                  {convType === "agent" && (Array.isArray(agents) ? agents : []).map((a) => (<SelectItem key={a.id} value={String(a.id)}>{`${a.id} · ${a.name}`}</SelectItem>))}
                                  {convType === "inbox" && (Array.isArray(inboxes) ? inboxes : []).map((i) => (<SelectItem key={i.id} value={String(i.id)}>{`${i.id} · ${i.name}`}</SelectItem>))}
                                  {convType === "team" && (Array.isArray(teams) ? teams : []).map((t) => (<SelectItem key={t.id} value={String(t.id)}>{`${t.id} · ${t.name}`}</SelectItem>))}
                                  {convType === "label" && (Array.isArray(labels) ? labels : []).map((l) => (<SelectItem key={l.id} value={String(l.id)}>{`${l.id} · ${l.name || l.title || "label"}`}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                        <Table>
                          <TableHeader><TableRow><TableHead>Timestamp</TableHead><TableHead>Valor</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {conversationsCount.isLoading && (<TableRow><TableCell>Carregando…</TableCell><TableCell>—</TableCell></TableRow>)}
                            {!conversationsCount.isLoading && (conversationsCount.data || []).map((r, i) => (
                              <TableRow key={i}><TableCell>{new Date(r.timestamp).toLocaleString()}</TableCell><TableCell>{r.value as any}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader><CardTitle>Incoming Messages</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid gap-3 md:grid-cols-4 mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Total</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(incomingMessagesCount.data || []).total)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Média</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(incomingMessagesCount.data || []).avg)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Pico</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(incomingMessagesCount.data || []).max)}</p>
                          </div>
                          <div>
                            <Sparkline data={incomingMessagesCount.data || []} />
                          </div>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2 mb-3">
                          <div>
                            <label className="text-sm">Tipo</label>
                            <Select value={inType} onValueChange={(v) => { setInType(v); setInId(""); }}>
                              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="account">account</SelectItem>
                                <SelectItem value="agent">agent</SelectItem>
                                <SelectItem value="inbox">inbox</SelectItem>
                                <SelectItem value="label">label</SelectItem>
                                <SelectItem value="team">team</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {inType !== "account" && (
                            <div>
                              <label className="text-sm">Entidade</label>
                              <Select value={inId} onValueChange={setInId}>
                                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent>
                                  {inType === "agent" && (Array.isArray(agents) ? agents : []).map((a) => (<SelectItem key={a.id} value={String(a.id)}>{`${a.id} · ${a.name}`}</SelectItem>))}
                                  {inType === "inbox" && (Array.isArray(inboxes) ? inboxes : []).map((i) => (<SelectItem key={i.id} value={String(i.id)}>{`${i.id} · ${i.name}`}</SelectItem>))}
                                  {inType === "team" && (Array.isArray(teams) ? teams : []).map((t) => (<SelectItem key={t.id} value={String(t.id)}>{`${t.id} · ${t.name}`}</SelectItem>))}
                                  {inType === "label" && (Array.isArray(labels) ? labels : []).map((l) => (<SelectItem key={l.id} value={String(l.id)}>{`${l.id} · ${l.name || l.title || "label"}`}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                        <Table>
                          <TableHeader><TableRow><TableHead>Timestamp</TableHead><TableHead>Valor</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {incomingMessagesCount.isLoading && (<TableRow><TableCell>Carregando…</TableCell><TableCell>—</TableCell></TableRow>)}
                            {!incomingMessagesCount.isLoading && (incomingMessagesCount.data || []).map((r, i) => (
                              <TableRow key={i}><TableCell>{new Date(r.timestamp).toLocaleString()}</TableCell><TableCell>{r.value as any}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader><CardTitle>Outgoing Messages</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid gap-3 md:grid-cols-4 mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Total</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(outgoingMessagesCount.data || []).total)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Média</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(outgoingMessagesCount.data || []).avg)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Pico</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(outgoingMessagesCount.data || []).max)}</p>
                          </div>
                          <div>
                            <Sparkline data={outgoingMessagesCount.data || []} />
                          </div>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2 mb-3">
                          <div>
                            <label className="text-sm">Tipo</label>
                            <Select value={outType} onValueChange={(v) => { setOutType(v); setOutId(""); }}>
                              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="account">account</SelectItem>
                                <SelectItem value="agent">agent</SelectItem>
                                <SelectItem value="inbox">inbox</SelectItem>
                                <SelectItem value="label">label</SelectItem>
                                <SelectItem value="team">team</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {outType !== "account" && (
                            <div>
                              <label className="text-sm">Entidade</label>
                              <Select value={outId} onValueChange={setOutId}>
                                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent>
                                  {outType === "agent" && (Array.isArray(agents) ? agents : []).map((a) => (<SelectItem key={a.id} value={String(a.id)}>{`${a.id} · ${a.name}`}</SelectItem>))}
                                  {outType === "inbox" && (Array.isArray(inboxes) ? inboxes : []).map((i) => (<SelectItem key={i.id} value={String(i.id)}>{`${i.id} · ${i.name}`}</SelectItem>))}
                                  {outType === "team" && (Array.isArray(teams) ? teams : []).map((t) => (<SelectItem key={t.id} value={String(t.id)}>{`${t.id} · ${t.name}`}</SelectItem>))}
                                  {outType === "label" && (Array.isArray(labels) ? labels : []).map((l) => (<SelectItem key={l.id} value={String(l.id)}>{`${l.id} · ${l.name || l.title || "label"}`}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                        <Table>
                          <TableHeader><TableRow><TableHead>Timestamp</TableHead><TableHead>Valor</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {outgoingMessagesCount.isLoading && (<TableRow><TableCell>Carregando…</TableCell><TableCell>—</TableCell></TableRow>)}
                            {!outgoingMessagesCount.isLoading && (outgoingMessagesCount.data || []).map((r, i) => (
                              <TableRow key={i}><TableCell>{new Date(r.timestamp).toLocaleString()}</TableCell><TableCell>{r.value as any}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader><CardTitle>Avg First Response Time</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid gap-3 md:grid-cols-4 mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Média</p>
                            <p className="text-lg font-semibold">{formatDuration(computeStats(avgFirstResponseTime.data || []).avg)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Pico</p>
                            <p className="text-lg font-semibold">{formatDuration(computeStats(avgFirstResponseTime.data || []).max)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Amostras</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(avgFirstResponseTime.data || []).count)}</p>
                          </div>
                          <div>
                            <Sparkline data={avgFirstResponseTime.data || []} />
                          </div>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2 mb-3">
                          <div>
                            <label className="text-sm">Tipo</label>
                            <Select value={afrType} onValueChange={(v) => { setAfrType(v); setAfrId(""); }}>
                              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="account">account</SelectItem>
                                <SelectItem value="agent">agent</SelectItem>
                                <SelectItem value="inbox">inbox</SelectItem>
                                <SelectItem value="label">label</SelectItem>
                                <SelectItem value="team">team</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {afrType !== "account" && (
                            <div>
                              <label className="text-sm">Entidade</label>
                              <Select value={afrId} onValueChange={setAfrId}>
                                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent>
                                  {afrType === "agent" && (Array.isArray(agents) ? agents : []).map((a) => (<SelectItem key={a.id} value={String(a.id)}>{`${a.id} · ${a.name}`}</SelectItem>))}
                                  {afrType === "inbox" && (Array.isArray(inboxes) ? inboxes : []).map((i) => (<SelectItem key={i.id} value={String(i.id)}>{`${i.id} · ${i.name}`}</SelectItem>))}
                                  {afrType === "team" && (Array.isArray(teams) ? teams : []).map((t) => (<SelectItem key={t.id} value={String(t.id)}>{`${t.id} · ${t.name}`}</SelectItem>))}
                                  {afrType === "label" && (Array.isArray(labels) ? labels : []).map((l) => (<SelectItem key={l.id} value={String(l.id)}>{`${l.id} · ${l.name || l.title || "label"}`}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                        <Table>
                          <TableHeader><TableRow><TableHead>Timestamp</TableHead><TableHead>Valor</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {avgFirstResponseTime.isLoading && (<TableRow><TableCell>Carregando…</TableCell><TableCell>—</TableCell></TableRow>)}
                            {!avgFirstResponseTime.isLoading && (avgFirstResponseTime.data || []).map((r, i) => (
                              <TableRow key={i}><TableCell>{new Date(r.timestamp).toLocaleString()}</TableCell><TableCell>{r.value as any}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader><CardTitle>Avg Resolution Time</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid gap-3 md:grid-cols-4 mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Média</p>
                            <p className="text-lg font-semibold">{formatDuration(computeStats(avgResolutionTime.data || []).avg)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Pico</p>
                            <p className="text-lg font-semibold">{formatDuration(computeStats(avgResolutionTime.data || []).max)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Amostras</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(avgResolutionTime.data || []).count)}</p>
                          </div>
                          <div>
                            <Sparkline data={avgResolutionTime.data || []} />
                          </div>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2 mb-3">
                          <div>
                            <label className="text-sm">Tipo</label>
                            <Select value={arType} onValueChange={(v) => { setArType(v); setArId(""); }}>
                              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="account">account</SelectItem>
                                <SelectItem value="agent">agent</SelectItem>
                                <SelectItem value="inbox">inbox</SelectItem>
                                <SelectItem value="label">label</SelectItem>
                                <SelectItem value="team">team</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {arType !== "account" && (
                            <div>
                              <label className="text-sm">Entidade</label>
                              <Select value={arId} onValueChange={setArId}>
                                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent>
                                  {arType === "agent" && (Array.isArray(agents) ? agents : []).map((a) => (<SelectItem key={a.id} value={String(a.id)}>{`${a.id} · ${a.name}`}</SelectItem>))}
                                  {arType === "inbox" && (Array.isArray(inboxes) ? inboxes : []).map((i) => (<SelectItem key={i.id} value={String(i.id)}>{`${i.id} · ${i.name}`}</SelectItem>))}
                                  {arType === "team" && (Array.isArray(teams) ? teams : []).map((t) => (<SelectItem key={t.id} value={String(t.id)}>{`${t.id} · ${t.name}`}</SelectItem>))}
                                  {arType === "label" && (Array.isArray(labels) ? labels : []).map((l) => (<SelectItem key={l.id} value={String(l.id)}>{`${l.id} · ${l.name || l.title || "label"}`}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                        <Table>
                          <TableHeader><TableRow><TableHead>Timestamp</TableHead><TableHead>Valor</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {avgResolutionTime.isLoading && (<TableRow><TableCell>Carregando…</TableCell><TableCell>—</TableCell></TableRow>)}
                            {!avgResolutionTime.isLoading && (avgResolutionTime.data || []).map((r, i) => (
                              <TableRow key={i}><TableCell>{new Date(r.timestamp).toLocaleString()}</TableCell><TableCell>{r.value as any}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader><CardTitle>Resolutions Count</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid gap-3 md:grid-cols-4 mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Total</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(resolutionsCount.data || []).total)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Média</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(resolutionsCount.data || []).avg)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Pico</p>
                            <p className="text-lg font-semibold">{fmt.format(computeStats(resolutionsCount.data || []).max)}</p>
                          </div>
                          <div>
                            <Sparkline data={resolutionsCount.data || []} />
                          </div>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2 mb-3">
                          <div>
                            <label className="text-sm">Tipo</label>
                            <Select value={resType} onValueChange={(v) => { setResType(v); setResId(""); }}>
                              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="account">account</SelectItem>
                                <SelectItem value="agent">agent</SelectItem>
                                <SelectItem value="inbox">inbox</SelectItem>
                                <SelectItem value="label">label</SelectItem>
                                <SelectItem value="team">team</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {resType !== "account" && (
                            <div>
                              <label className="text-sm">Entidade</label>
                              <Select value={resId} onValueChange={setResId}>
                                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent>
                                  {resType === "agent" && (Array.isArray(agents) ? agents : []).map((a) => (<SelectItem key={a.id} value={String(a.id)}>{`${a.id} · ${a.name}`}</SelectItem>))}
                                  {resType === "inbox" && (Array.isArray(inboxes) ? inboxes : []).map((i) => (<SelectItem key={i.id} value={String(i.id)}>{`${i.id} · ${i.name}`}</SelectItem>))}
                                  {resType === "team" && (Array.isArray(teams) ? teams : []).map((t) => (<SelectItem key={t.id} value={String(t.id)}>{`${t.id} · ${t.name}`}</SelectItem>))}
                                  {resType === "label" && (Array.isArray(labels) ? labels : []).map((l) => (<SelectItem key={l.id} value={String(l.id)}>{`${l.id} · ${l.name || l.title || "label"}`}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                        <Table>
                          <TableHeader><TableRow><TableHead>Timestamp</TableHead><TableHead>Valor</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {resolutionsCount.isLoading && (<TableRow><TableCell>Carregando…</TableCell><TableCell>—</TableCell></TableRow>)}
                            {!resolutionsCount.isLoading && (resolutionsCount.data || []).map((r, i) => (
                              <TableRow key={i}><TableCell>{new Date(r.timestamp).toLocaleString()}</TableCell><TableCell>{r.value as any}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="records">
          <Card>
            <CardHeader>
              <CardTitle>Registros</CardTitle>
              <CardDescription>Navegue por entidades do Chatwoot</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-4 gap-4">
                <div className="flex md:flex-col gap-2">
                  <Button variant={selectedEntity === "account" ? "default" : "outline"} onClick={() => setSelectedEntity("account")}>Conta Chatwoot</Button>
                  <Button variant={selectedEntity === "profiles" ? "default" : "outline"} onClick={() => setSelectedEntity("profiles")}>Perfil</Button>
                  <Button variant={selectedEntity === "users" ? "default" : "outline"} onClick={() => setSelectedEntity("users")}>Usuários</Button>
                  <Button variant={selectedEntity === "agentbots" ? "default" : "outline"} onClick={() => setSelectedEntity("agentbots")}>Atendentes IA</Button>
                  <Button variant={selectedEntity === "teams" ? "default" : "outline"} onClick={() => setSelectedEntity("teams")}>Times</Button>
                  <Button variant={selectedEntity === "agents" ? "default" : "outline"} onClick={() => setSelectedEntity("agents")}>Atendentes</Button>
                </div>
                <div className="md:col-span-3">
                  <Card className="border-dashed">
                    <CardContent>
                      {selectedEntity === "account" && (
                        <div className="space-y-3">
                          <div>
                            <h3 className="text-lg font-semibold">Conta Chatwoot</h3>
                            <p className="text-sm text-muted-foreground">Editar dados da conta vinculada ao tenant</p>
                          </div>
                          {!cfg?.token || !cfg?.accountId ? (
                            <Badge variant="destructive">Token ou Account ID ausente</Badge>
                          ) : (
                            <>
                              <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                  <label className="text-sm">Nome</label>
                                  <Input value={accountEdit.name || ""} onChange={(e) => setAccountEdit((s) => ({ ...s, name: e.target.value }))} />
                                </div>
                                <div>
                                  <label className="text-sm">Support Email</label>
                                  <Input value={accountEdit.support_email || ""} onChange={(e) => setAccountEdit((s) => ({ ...s, support_email: e.target.value }))} />
                                </div>
                                <div>
                                  <label className="text-sm">Locale</label>
                                  <Select value={accountEdit.locale || "pt_BR"} onValueChange={(v) => setAccountEdit((s) => ({ ...s, locale: v }))}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="pt_BR" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="en">en</SelectItem>
                                      <SelectItem value="pt_BR">pt_BR</SelectItem>
                                      <SelectItem value="es">es</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="text-sm">Domain</label>
                                  <Input value={accountEdit.domain || ""} onChange={(e) => setAccountEdit((s) => ({ ...s, domain: e.target.value }))} />
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={async () => {
                                    try {
                                      await sdk.appProxy({
                                        path: `/api/v1/accounts/${cfg!.accountId}`,
                                        method: "PATCH",
                                        token: cfg!.token as string,
                                        body: {
                                          name: accountEdit.name || undefined,
                                          support_email: accountEdit.support_email || undefined,
                                          locale: accountEdit.locale || undefined,
                                          domain: accountEdit.domain || undefined,
                                        },
                                      });
                                      queryClient.invalidateQueries({ queryKey: ["chatwoot-account", cfg?.accountId, cfg?.token] });
                                      toast.success("Conta atualizada");
                                    } catch (e: any) {
                                      toast.error(e.message || "Erro ao atualizar");
                                    }
                                  }}
                                >
                                  Salvar
                                </Button>
                              </div>
                              
                            </>
                          )}
                        </div>
                      )}
                      {selectedEntity === "users" && (
                        <div className="border rounded-md p-3 text-sm">
                          <div className="mb-2">
                            <h3 className="text-lg font-semibold">Usuários e Agentes</h3>
                            <p className="text-sm text-muted-foreground">Gerencie usuários, papéis e disponibilidade da conta</p>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <label className="text-sm">Nome</label>
                              <Input value={newPlatformUser.name} onChange={(e) => setNewPlatformUser((s) => ({ ...s, name: e.target.value }))} />
                            </div>
                            <div>
                              <label className="text-sm">Display Name</label>
                              <Input value={newPlatformUser.display_name} onChange={(e) => setNewPlatformUser((s) => ({ ...s, display_name: e.target.value }))} />
                            </div>
                            <div>
                              <label className="text-sm">Email</label>
                              <Input type="email" value={newPlatformUser.email} onChange={(e) => setNewPlatformUser((s) => ({ ...s, email: e.target.value }))} disabled={!!editingUserId} />
                              {newPlatformUser.email && !isValidEmail(newPlatformUser.email) && (
                                <div className="text-xs text-destructive mt-1">Email inválido</div>
                              )}
                            </div>
                            <div>
                              <label className="text-sm">Senha</label>
                              <Input type="password" value={newPlatformUser.password} onChange={(e) => { setPasswordError(""); setNewPlatformUser((s) => ({ ...s, password: e.target.value })); }} disabled={!!editingUserId} />
                              </div>
                              {newPlatformUser.password && !isStrongPassword(newPlatformUser.password) && (
                                <div className="text-xs text-destructive mt-1">Senha deve ter 6+ caracteres, 1 maiúscula, 1 número e 1 símbolo</div>
                              )}
                              {passwordError && (
                                <div className="text-xs text-destructive mt-1">{passwordError}</div>
                              )}
                            <div>
                              <label className="text-sm">Papel</label>
                              <Select value={linkRole} onValueChange={setLinkRole}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Administrador" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="administrator">Administrador</SelectItem>
                                  <SelectItem value="agent">Agente</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            {editingUserId ? (
                              <>
                                <Button disabled={saveUserMutation.isPending} onClick={() => saveUserMutation.mutate()}>
                                  {saveUserMutation.isPending ? (<span className="inline-flex items-center"><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Salvar…</span>) : ("Salvar")}
                                </Button>
                                <Button variant="outline" disabled={saveUserMutation.isPending} onClick={() => { setEditingUserId(null); setNewPlatformUser({ name: "", email: "", password: "", display_name: "" }); }}>Cancelar</Button>
                              </>
                            ) : (
                              <Button disabled={!newPlatformUser.name || !newPlatformUser.email || !newPlatformUser.password || !isValidEmail(newPlatformUser.email) || !isStrongPassword(newPlatformUser.password) || !!passwordError} onClick={() => createPlatformUserMutation.mutate()}>Criar e vincular</Button>
                            )}
                          </div>
                          <div className="mt-6 border rounded-md p-3">
                          <div className="mt-3 border rounded-md">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Usuário</TableHead>
                                  <TableHead>Nome</TableHead>
                                  <TableHead>Email</TableHead>
                                  <TableHead>Papel</TableHead>
                                  <TableHead>Disponibilidade</TableHead>
                                  <TableHead>Ações</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {(accountUsersLoading || agentsLoading) ? (
                                  <TableRow>
                                    <TableCell colSpan={6}>Carregando…</TableCell>
                                  </TableRow>
                                ) : (combinedAccountAgentsRows || []).length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={6} className="text-center text-muted-foreground">Nenhum usuário vinculado</TableCell>
                                  </TableRow>
                                ) : (
                                  combinedAccountAgentsRows.map((row) => (
                                    <TableRow key={row.user_id}>
                                      <TableCell>{row.user_id}</TableCell>
                                      <TableCell>{row.name}</TableCell>
                                      <TableCell>{row.email}</TableCell>
                                      <TableCell>
                                        <Select defaultValue={row.role} onValueChange={(v) => updateAgentRoleMutation.mutate({ id: row.user_id, role: v as any })}>
                                          <SelectTrigger>
                                            <SelectValue placeholder={displayRole(row.role)} />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="administrator">Administrador</SelectItem>
                                            <SelectItem value="agent">Agente</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </TableCell>
                                      <TableCell>
                                        <Select defaultValue={row.availability_status} onValueChange={(v) => updateAgentAvailabilityMutation.mutate({ id: row.user_id, availability_status: v })}>
                                          <SelectTrigger>
                                            <SelectValue placeholder={displayAvailability(row.availability_status)} />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="available">Disponível</SelectItem>
                                            <SelectItem value="busy">Ocupado</SelectItem>
                                            <SelectItem value="offline">Offline</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex gap-2">
                                          <Button variant="outline" onClick={() => { setEditingAgentId(row.user_id); setNewAgent({ name: row.name || "", email: row.email || "", role: row.role || "agent", availability_status: row.availability_status || "available", auto_offline: true }); }}>Editar</Button>
                                          <Button variant="destructive" onClick={() => { if (window.confirm("Remover este agente do account?")) deleteAgentMutation.mutate(row.user_id); }}>Remover</Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </div>
                          </div>
                        </div>
                      )}
                      {selectedEntity === "agents" && (
                        <div className="border rounded-md">
                          <div className="p-3">
                            <h3 className="text-lg font-semibold">Agentes</h3>
                            <p className="text-sm text-muted-foreground">Criar, editar papel e disponibilidade</p>
                          </div>
                          <div className="grid gap-3 md:grid-cols-3 p-3">
                            <div>
                              <label className="text-sm">Nome</label>
                              <Input value={newAgent.name} onChange={(e) => setNewAgent((s) => ({ ...s, name: e.target.value }))} />
                            </div>
                            <div>
                              <label className="text-sm">Email</label>
                              <Input type="email" value={newAgent.email} onChange={(e) => setNewAgent((s) => ({ ...s, email: e.target.value }))} />
                              {newAgent.email && !isValidEmail(newAgent.email) && (
                                <div className="text-xs text-destructive mt-1">Email inválido</div>
                              )}
                            </div>
                            <div>
                              <label className="text-sm">Role</label>
                              <Select value={newAgent.role} onValueChange={(v) => setNewAgent((s) => ({ ...s, role: v }))}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Agente" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="agent">Agente</SelectItem>
                                  <SelectItem value="administrator">Administrador</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-sm">Disponibilidade</label>
                              <Select value={newAgent.availability_status || "available"} onValueChange={(v) => setNewAgent((s) => ({ ...s, availability_status: v }))}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Disponível" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="available">Disponível</SelectItem>
                                  <SelectItem value="busy">Ocupado</SelectItem>
                                  <SelectItem value="offline">Offline</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-end gap-2">
                              {editingAgentId ? (
                                <>
                                  <Button onClick={() => saveAgentRoleMutation.mutate()}>Salvar</Button>
                                  <Button
                                    variant="destructive"
                                    onClick={() => { if (window.confirm("Remover este agente do account?")) deleteAgentMutation.mutate(editingAgentId); }}
                                  >
                                    Excluir
                                  </Button>
                                  <Button variant="outline" onClick={() => { setEditingAgentId(null); setNewAgent({ name: "", email: "", role: "agent", availability_status: "available", auto_offline: true }); }}>Cancelar</Button>
                                </>
                              ) : (
                                <Button disabled={!newAgent.name || !newAgent.email || !isValidEmail(newAgent.email)} onClick={() => createAgentMutation.mutate()}>Criar agente</Button>
                              )}
                            </div>
                          </div>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>Nome</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {agentsLoading && (
                                <TableRow>
                                  <TableCell>Carregando…</TableCell>
                                  <TableCell>—</TableCell>
                                  <TableCell>—</TableCell>
                                  <TableCell>—</TableCell>
                                  <TableCell>—</TableCell>
                                </TableRow>
                              )}
                              {!agentsLoading && (agents || []).map((a) => (
                                <TableRow key={a.id} onClick={() => { setEditingAgentId(a.id); setNewAgent({ name: a.name || "", email: a.email || "", role: a.role || "agent", availability_status: a.availability_status || "available", auto_offline: a.auto_offline }); }} className="cursor-pointer">
                                  <TableCell>{a.id}</TableCell>
                                  <TableCell>{a.name}</TableCell>
                                  <TableCell>{a.email}</TableCell>
                                  <TableCell>{displayRole(a.role)}</TableCell>
                                  <TableCell>{displayAvailability(a.availability_status)}</TableCell>
                                  <TableCell>
                                    <div className="flex gap-2">
                                      <Button variant="outline" onClick={() => { setEditingAgentId(a.id); setNewAgent({ name: a.name || "", email: a.email || "", role: a.role || "agent", availability_status: a.availability_status || "available", auto_offline: a.auto_offline }); }}>Editar</Button>
                                      <Button variant="destructive" onClick={() => { if (window.confirm("Remover este agente do account?")) deleteAgentMutation.mutate(a.id); }}>Excluir</Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                      {selectedEntity === "teams" && (
                        <div className="border rounded-md p-2">
                          <div className="mb-1">
                            <h3 className="text-base font-semibold">Times</h3>
                            <p className="text-xs text-muted-foreground">Gerencie times e seus membros</p>
                          </div>
                          <div className="grid gap-2 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                            {teamsLoading && (<div className="text-sm text-muted-foreground p-3">Carregando…</div>)}
                            {!teamsLoading && (teams || []).map((t) => (
                              <Card key={t.id} className="cursor-pointer" onClick={() => { setEditingTeamId(t.id); setSelectedTeam(t.id); setNewTeam({ name: t.name || "", description: t.description || "" }); }}>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 py-2">
                                  <div>
                                    <CardTitle className="text-sm">{t.name}</CardTitle>
                                    <CardDescription className="text-xs">{`ID ${t.id}`}</CardDescription>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button variant="outline" className="h-8 w-8" title="Editar" onClick={(e) => { e.stopPropagation(); setEditingTeamId(t.id); setSelectedTeam(t.id); setNewTeam({ name: t.name || "", description: t.description || "" }); }}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"></path><path d="m15 5 4 4"></path></svg>
                                    </Button>
                                    <Button variant="outline" className="h-8 w-8 text-red-600 hover:text-white hover:bg-red-500" title="Excluir" onClick={(e) => { e.stopPropagation(); if (window.confirm("Excluir este team?")) deleteTeamMutation.mutate(t.id); }}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                    </Button>
                                  </div>
                                </CardHeader>
                                <CardContent className="py-2">
                                  {t.description ? (
                                    <div className="text-xs">{t.description}</div>
                                  ) : null}
                                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                                    <span className="text-xs text-muted-foreground">Atendentes:</span>
                                    {(teamMembersMap[t.id] || []).length === 0 ? (
                                      <Badge variant="secondary">Nenhum</Badge>
                                    ) : (
                                      (teamMembersMap[t.id] || []).map((uid) => (
                                        <Badge key={`${t.id}-${uid}`} variant="outline">{agentById[uid]?.name || String(uid)}</Badge>
                                      ))
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                          <div className="shrink-0 bg-border h-[1px] w-full my-2" role="none" data-orientation="horizontal" />
                          <div className="text-xs text-muted-foreground">Dica: use nomes curtos e claros para facilitar a busca.</div>
                          <div className="p-2 space-y-2">
                            <div className="grid gap-2 md:grid-cols-3">
                              <div>
                                <label className="text-sm">Nome</label>
                                <Input value={newTeam.name} onChange={(e) => setNewTeam((s) => ({ ...s, name: e.target.value }))} />
                              </div>
                              <div className="md:col-span-2">
                                <label className="text-sm">Descrição</label>
                                <Input value={newTeam.description || ""} onChange={(e) => setNewTeam((s) => ({ ...s, description: e.target.value }))} />
                              </div>
                            </div>
                            {editingTeamId && (
                              <div className="mt-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <label className="text-sm">Agentes no Team</label>
                                  {selectedTeam && <Badge variant="secondary">Team {selectedTeam}</Badge>}
                                </div>
                                <div className="grid md:grid-cols-3 gap-2">
                                  {(agents || []).map((a) => (
                                    <label key={a.id} className="flex items-center gap-2 text-sm">
                                      <input type="checkbox" checked={!!selectedAgentIds[a.id]} onChange={(e) => setSelectedAgentIds((s) => ({ ...s, [a.id]: e.target.checked }))} />
                                      <span>{`${a.id} · ${a.name}`}</span>
                                    </label>
                                  ))}
                                </div>
                                <div className="text-xs text-muted-foreground">Selecionados: {Object.values(selectedAgentIds).filter(Boolean).length}</div>
                              </div>
                            )}
                            <div className="mt-2 flex items-end justify-end gap-2">
                              {editingTeamId ? (
                                <>
                                  <Button onClick={() => updateTeamMutation.mutate()}>Salvar</Button>
                                  <Button variant="destructive" onClick={() => { if (window.confirm("Excluir este team?")) deleteTeamMutation.mutate(editingTeamId); }}>Excluir</Button>
                                  <Button variant="outline" onClick={() => { setEditingTeamId(null); setNewTeam({ name: "", description: "" }); setSelectedAgentIds({}); }}>Cancelar</Button>
                                </>
                              ) : (
                                  <Button disabled={!newTeam.name} onClick={() => createTeamMutation.mutate()}>Criar team</Button>
                              )}
                            </div>
                          </div>
                          </div>
                      )}
                      {selectedEntity === "inboxes" && (
                        <div className="border rounded-md">
                          <div className="p-3">
                            <h3 className="text-lg font-semibold">Inboxes</h3>
                            <p className="text-sm text-muted-foreground">Associação de agentes e bots</p>
                          </div>
                          <div className="flex justify-end p-2">
                            <Button
                              variant="outline"
                              onClick={async () => {
                                try {
                                  const entries = await Promise.all(
                                    (inboxes || []).map(async (i) => {
                                      const data = await sdk.appProxy<{ id: number; name: string }>({
                                        path: `/api/v1/accounts/${cfg!.accountId}/inboxes/${i.id}/agent_bot`,
                                        method: "GET",
                                        token: cfg!.token as string,
                                      });
                                      return [i.id, { id: data.id, name: data.name }] as const;
                                    })
                                  );
                                  const map: Record<number, { id: number; name: string } | null> = {};
                                  for (const [id, bot] of entries) map[id] = bot;
                                  setInboxBotMap(map);
                                  toast.success("Bots atualizados");
                                } catch (e: any) {
                                  toast.error(e.message || "Erro ao consultar bots");
                                }
                              }}
                            >
                              Atualizar bots
                            </Button>
                          </div>
                          <div className="p-3">
                            <div className="grid gap-3 md:grid-cols-3">
                              <div>
                                <label className="text-sm">Inbox</label>
                                <Select onValueChange={(v) => setSelectedInbox(Number(v))}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione a inbox" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {inboxesLoading && <div className="p-2 text-xs text-muted-foreground">Carregando…</div>}
                                    {!inboxesLoading && (Array.isArray(inboxes) ? inboxes : []).length === 0 && (
                                      <div className="p-2 text-xs text-muted-foreground">Nenhuma inbox encontrada</div>
                                    )}
                                    {(Array.isArray(inboxes) ? inboxes : []).map((i) => (
                                      <SelectItem key={i.id} value={String(i.id)}>{`${i.id} · ${i.name}`}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-sm">Instância Evolution</label>
                                <Input value={evoInstance} onChange={(e) => setEvoInstance(e.target.value)} placeholder="ex: 5534999000000" />
                              </div>
                              <div className="flex items-end gap-2">
                                <Button onClick={fetchEvolutionQr} disabled={evoLoading}>
                                  <QrCode className="h-4 w-4 mr-1" />
                                  {evoLoading ? "Gerando…" : "Gerar QR"}
                                </Button>
                              </div>
                            </div>
                            {(evoQrDataUrl || evoPairingCode) && (
                              <div className="mt-3 grid gap-3 md:grid-cols-3">
                                <div className="flex items-center justify-center border rounded-md p-3">
                                  {evoQrDataUrl ? (
                                    <img src={evoQrDataUrl} alt="QR Code" className="w-56 h-56" />
                                  ) : (
                                    <div className="text-sm text-muted-foreground">QR não disponível</div>
                                  )}
                                </div>
                                <div className="md:col-span-2 border rounded-md p-3">
                                  <div className="text-sm">Código de pareamento</div>
                                  <div className="text-2xl font-mono tracking-widest">{evoPairingCode || "—"}</div>
                                  <div className="text-xs text-muted-foreground mt-2">Abra o WhatsApp → Aparelhos conectados → Conectar aparelho e escaneie o QR ou use o código de pareamento.</div>
                                </div>
                              </div>
                            )}
                            <div className="mt-3">
                              <label className="text-sm">Agentes na Inbox</label>
                              <div className="grid md:grid-cols-3 gap-2 mt-1">
                                {(agents || []).map((a) => (
                                  <label key={a.id} className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" onChange={(e) => {
                                      const key = `inbox-${selectedInbox || 0}-user-${a.id}`;
                                      (window as any)[key] = e.target.checked;
                                    }} />
                                    <span>{`${a.id} · ${a.name}`}</span>
                                  </label>
                                ))}
                              </div>
                              <div className="flex gap-2 mt-2">
                                <Button
                                  variant="outline"
                                  onClick={async () => {
                                    if (!selectedInbox) { toast.error("Selecione a inbox"); return; }
                                    try {
                                      setInboxMembersLoading(true);
                                      const data = await sdk.appProxy<{ payload: Array<{ id: number }> }>({
                                        path: `/api/v1/accounts/${cfg!.accountId}/inbox_members/${selectedInbox}`,
                                        method: "GET",
                                        token: cfg!.token as string,
                                      });
                                      setInboxMembersLoading(false);
                                      const ids: number[] = (data?.payload || []).map((u: any) => u.id);
                                      for (const a of (agents || [])) {
                                        const key = `inbox-${selectedInbox}-user-${a.id}`;
                                        (window as any)[key] = ids.includes(a.id);
                                      }
                                      toast.success("Membros carregados");
                                    } catch (e: any) {
                                      setInboxMembersLoading(false);
                                      toast.error(e.message || "Erro ao listar membros");
                                    }
                                  }}
                                >
                                  Carregar membros atuais
                                </Button>
                                <Button
                                  onClick={async () => {
                                    if (!selectedInbox) { toast.error("Selecione a inbox"); return; }
                                    const ids = (agents || [])
                                      .filter((a) => (window as any)[`inbox-${selectedInbox}-user-${a.id}`])
                                      .map((a) => a.id);
                                    if (ids.length === 0) { toast.error("Selecione ao menos um agente"); return; }
                                    try {
                                      await sdk.appProxy({
                                        path: `/api/v1/accounts/${cfg!.accountId}/inbox_members`,
                                        method: "POST",
                                        token: cfg!.token as string,
                                        body: { inbox_id: selectedInbox, user_ids: ids },
                                      });
                                      toast.success("Agentes adicionados à inbox");
                                    } catch (e: any) { toast.error(e.message || "Erro ao adicionar"); }
                                  }}
                                >
                                  Adicionar agentes
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={async () => {
                                    if (!selectedInbox) { toast.error("Selecione a inbox"); return; }
                                    const ids = (agents || [])
                                      .filter((a) => (window as any)[`inbox-${selectedInbox}-user-${a.id}`])
                                      .map((a) => a.id);
                                    if (ids.length === 0) { toast.error("Selecione ao menos um agente"); return; }
                                    try {
                                      await sdk.appProxy({
                                        path: `/api/v1/accounts/${cfg!.accountId}/inbox_members`,
                                        method: "PATCH",
                                        token: cfg!.token as string,
                                        body: { inbox_id: selectedInbox, user_ids: ids },
                                      });
                                      toast.success("Inbox atualizada");
                                    } catch (e: any) { toast.error(e.message || "Erro ao atualizar"); }
                                  }}
                                >
                                  Atualizar agentes
                                </Button>
                                <div className="flex-1" />
                                <div className="flex items-center gap-2">
                                  <label className="text-sm">Bot</label>
                                  <Select onValueChange={(v) => setSelectedAgentBot(Number(v))}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Selecione o bot" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(accountAgentBots || []).map((b: any) => (
                                        <SelectItem key={b.id} value={String(b.id)}>{`${b.id} · ${b.name}`}</SelectItem>
                                      ))}
                                      <SelectItem value="0">Nenhum</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    onClick={async () => {
                                      if (!selectedInbox) { toast.error("Selecione a inbox"); return; }
                                      const text = selectedAgentBot ? `Associar bot ${selectedAgentBot} à inbox ${selectedInbox}?` : `Remover bot da inbox ${selectedInbox}?`;
                                      if (!window.confirm(text)) return;
                                      try { await associateAgentBotMutation.mutateAsync({ inboxId: selectedInbox, agentBotId: selectedAgentBot || null }); } catch (e: any) { toast.error(e.message || "Erro na associação"); }
                                    }}
                                  >
                                    Salvar bot
                                  </Button>
                                </div>
                              </div>
                              {inboxMembersLoading && <div className="text-xs text-muted-foreground mt-2">Carregando membros…</div>}
                            </div>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>Nome</TableHead>
                                <TableHead>Canal</TableHead>
                                <TableHead>Bot</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {inboxesLoading && (
                                <TableRow>
                                  <TableCell>Carregando…</TableCell>
                                  <TableCell>—</TableCell>
                                  <TableCell>—</TableCell>
                                  <TableCell>—</TableCell>
                                </TableRow>
                              )}
                              {!inboxesLoading && (inboxes || []).map((i) => (
                                <TableRow key={i.id}>
                                  <TableCell>{i.id}</TableCell>
                                  <TableCell>{i.name}</TableCell>
                                  <TableCell>{i.channel_type}</TableCell>
                                  <TableCell>{inboxBotMap[i.id]?.name || "—"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          </div>
                        </div>
                      )}
                      {selectedEntity === "agentbots" && (
                        <div className="border rounded-md p-3">
                          <div className="mb-2">
                            <h3 className="text-lg font-semibold">Atendentes IA</h3>
                            <p className="text-sm text-muted-foreground">Criar e gerenciar bots e webhooks</p>
                          </div>
                          <div className="grid gap-3 md:grid-cols-3">
                            <div>
                              <label className="text-sm">Nome do Bot</label>
                              <Input value={newBot.name} onChange={(e) => setNewBot((s) => ({ ...s, name: e.target.value }))} />
                            </div>
                            <div className="md:col-span-2">
                              <label className="text-sm">Webhook de saída</label>
                              <Input value={newBot.outgoing_url} onChange={(e) => setNewBot((s) => ({ ...s, outgoing_url: e.target.value }))} />
                              {newBot.outgoing_url && !isValidUrlOrEmpty(newBot.outgoing_url) && (
                                <div className="text-xs text-destructive mt-1">URL inválida</div>
                              )}
                            </div>
                            <div className="flex items-end gap-2">
                              {editingBotId ? (
                                <>
                                  <Button onClick={() => updateBotMutation.mutate()} disabled={updateBotMutation.isPending}>
                                    {updateBotMutation.isPending ? (<span className="inline-flex items-center"><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Salvando…</span>) : ("Salvar")}
                                  </Button>
                                  <Button variant="destructive" onClick={() => { if (window.confirm("Excluir este bot?")) deleteBotMutation.mutate(editingBotId); }} disabled={deleteBotMutation.isPending}>
                                    {deleteBotMutation.isPending ? (<span className="inline-flex items-center"><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Excluindo…</span>) : ("Excluir")}
                                  </Button>
                                  <Button variant="outline" onClick={() => { setEditingBotId(null); setNewBot({ name: "", outgoing_url: "" }); }}>Cancelar</Button>
                                </>
                              ) : (
                                <Button disabled={!newBot.name || !isValidUrlOrEmpty(newBot.outgoing_url) || createBotMutation.isPending} onClick={() => createBotMutation.mutate()}>
                                  {createBotMutation.isPending ? (<span className="inline-flex items-center"><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Criando…</span>) : ("Criar bot")}
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="mt-4 border rounded-md">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>ID</TableHead>
                                  <TableHead>Nome</TableHead>
                                  <TableHead>Webhook</TableHead>
                                  <TableHead>Token</TableHead>
                                  <TableHead>Ações</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {accountAgentBotsLoading && (
                                  <TableRow>
                                    <TableCell>Carregando…</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell>—</TableCell>
                                  </TableRow>
                                )}
                                {!accountAgentBotsLoading && (accountAgentBots || []).map((b) => (
                                  <TableRow key={b.id} onClick={() => { setEditingBotId(b.id); setNewBot({ name: b.name || "", outgoing_url: b.outgoing_url || "" }); }} className="cursor-pointer">
                                    <TableCell>{b.id}</TableCell>
                                    <TableCell>{b.name}</TableCell>
                                    <TableCell className="break-all">{b.outgoing_url || ""}</TableCell>
                                    <TableCell className="break-all">{(b as any).token || "—"}</TableCell>
                                    <TableCell>
                                      <div className="flex gap-2">
                                        <Button variant="outline" onClick={() => { setEditingBotId(b.id); setNewBot({ name: b.name || "", outgoing_url: b.outgoing_url || "" }); }} disabled={updateBotMutation.isPending}>Editar</Button>
                                        <Button variant="destructive" onClick={() => { if (window.confirm("Excluir este bot?")) deleteBotMutation.mutate(b.id); }} disabled={deleteBotMutation.isPending}>
                                          {deleteBotMutation.isPending ? (<span className="inline-flex items-center"><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Excluindo…</span>) : ("Excluir")}
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}
                      {selectedEntity === "profiles" && (
                        <div className="border rounded-md p-3 text-sm">
                          {profileLoading ? (
                            "Carregando…"
                          ) : (
                            <div className="space-y-4">
                              <div>
                                <h3 className="text-lg font-semibold">Perfil</h3>
                                <p className="text-sm text-muted-foreground">Informações do usuário autenticado na integração</p>
                              </div>
                              <div className="grid gap-3 md:grid-cols-4">
                                <div>
                                  <label className="text-xs text-muted-foreground">ID</label>
                                  <div className="text-sm font-medium">{profile?.id}</div>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground">Nome</label>
                                  <div className="text-sm font-medium">{profile?.name || profile?.display_name}</div>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground">Email</label>
                                  <div className="text-sm font-medium">{profile?.email}</div>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground">Papel</label>
                                  <div className="text-sm font-medium">{displayRolePT(profile?.role)}</div>
                                </div>
                              </div>
                              <div className="grid gap-3 md:grid-cols-4">
                                <div>
                                  <label className="text-xs text-muted-foreground">Provider</label>
                                  <div className="text-sm">{profile?.provider}</div>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground">Confirmado</label>
                                  <div className="text-sm">{yesNo(profile?.confirmed)}</div>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground">Disponibilidade</label>
                                  <div className="text-sm">{typeof (profile as any)?.availability === "boolean" ? yesNo((profile as any)?.availability) : typeof (profile as any)?.availability_status === "boolean" ? yesNo((profile as any)?.availability_status) : (profile as any)?.availability || (profile as any)?.availability_status}</div>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground">Avatar</label>
                                  <div className="flex items-center gap-3">
                                    {profile?.avatar_url ? (
                                      <img src={profile.avatar_url as any} alt="Avatar" className="w-16 h-16 rounded-full object-cover border" />
                                    ) : (
                                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center text-sm">—</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="mt-2">
                                <label className="text-xs text-muted-foreground">Conta do tenant</label>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>ID</TableHead>
                                      <TableHead>Nome</TableHead>
                                      <TableHead>Papel</TableHead>
                                      <TableHead>Status</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {(() => {
                                      const list = Array.isArray(profile?.accounts) ? profile.accounts : [];
                                      const sel = list.find((a: any) => Number(a?.id ?? a?.account_id) === Number(cfg?.accountId));
                                      if (!sel) return (
                                        <TableRow><TableCell colSpan={4}>Nenhuma conta vinculada ao tenant atual</TableCell></TableRow>
                                      );
                                      return (
                                        <TableRow key={sel.id}>
                                          <TableCell>{sel.id}</TableCell>
                                          <TableCell>{sel.name}</TableCell>
                                          <TableCell>{displayRolePT(sel.role)}</TableCell>
                                          <TableCell>{displayActivePT(sel.status)}</TableCell>
                                        </TableRow>
                                      );
                                    })()}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <ChatwootAudit />
        </TabsContent>

        <TabsContent value="webhooks">
              <Card>
                <CardHeader>
                  <CardTitle>Webhooks</CardTitle>
                  <CardDescription>Gerencie assinaturas de eventos</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!cfg?.token || !cfg?.accountId ? (
                    <Badge variant="destructive">Token ou Account ID ausente</Badge>
                  ) : (
                    <>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <label className="text-sm">URL</label>
                          <Input value={whUrl} onChange={(e) => setWhUrl(e.target.value)} placeholder="https://exemplo.com/webhook" />
                        </div>
                        <div>
                          <label className="text-sm">Assinaturas</label>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            {AVAILABLE_WEBHOOK_SUBS.map((s) => (
                              <label key={s} className="flex items-center gap-2 text-xs">
                                <input type="checkbox" checked={!!whSubsSelected[s]} onChange={(e) => setWhSubsSelected((prev) => ({ ...prev, [s]: e.target.checked }))} />
                                <span>{s}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            const subs = AVAILABLE_WEBHOOK_SUBS.filter((s) => !!whSubsSelected[s]);
                            if (!whUrl || subs.length === 0) {
                              toast.error("Preencha URL e subscriptions");
                              return;
                            }
                            createWebhookMutation.mutate({ url: whUrl, subscriptions: subs });
                          }}
                          disabled={createWebhookMutation.isPending}
                        >
                          {createWebhookMutation.isPending ? (<span className="inline-flex items-center"><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Criando…</span>) : ("Criar webhook")}
                        </Button>
                      </div>
                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>ID</TableHead>
                              <TableHead>URL</TableHead>
                              <TableHead>Subscriptions</TableHead>
                              <TableHead>Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {webhooksLoading && (
                              <TableRow>
                                <TableCell>Carregando…</TableCell>
                                <TableCell>—</TableCell>
                                <TableCell>—</TableCell>
                                <TableCell>—</TableCell>
                              </TableRow>
                            )}
                            {!webhooksLoading && (Array.isArray(webhooks) ? webhooks : []).map((w) => (
                              <TableRow key={w.id}>
                                <TableCell>{w.id}</TableCell>
                                <TableCell>{w.url}</TableCell>
                                <TableCell>{(w.subscriptions || []).join(", ")}</TableCell>
                                <TableCell>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    const subs = AVAILABLE_WEBHOOK_SUBS.filter((s) => !!whSubsSelected[s]);
                                    updateWebhookMutation.mutate({ id: w.id, url: w.url, subscriptions: subs.length ? subs : (w.subscriptions || []) });
                                  }}
                                  disabled={updateWebhookMutation.isPending}
                                >
                                  {updateWebhookMutation.isPending ? (<span className="inline-flex items-center"><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Atualizando…</span>) : ("Atualizar")}
                                </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
        </TabsContent>
        <TabsContent value="integration">
              <Card>
                <CardHeader>
                  <CardTitle>Integrações Chatwoot</CardTitle>
                  <CardDescription>Atendentes IA e associações</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!cfg?.token || !cfg?.accountId ? (
                    <Badge variant="destructive">Token ou Account ID ausente</Badge>
                  ) : (
                    <>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <label className="text-sm">Inbox</label>
                          <Select onValueChange={(v) => setSelectedInbox(Number(v))}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a inbox" />
                            </SelectTrigger>
                            <SelectContent>
                              {(inboxes || []).map((i) => (
                                <SelectItem key={i.id} value={String(i.id)}>{`${i.id} · ${i.name}`}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm">AgentBot</label>
                          <Select onValueChange={(v) => setSelectedAgentBot(Number(v))}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o bot" />
                            </SelectTrigger>
                            <SelectContent>
                              {(accountAgentBots || []).map((b) => (
                                <SelectItem key={b.id} value={String(b.id)}>{`${b.id} · ${b.name}`}</SelectItem>
                              ))}
                              <SelectItem value="0">Remover</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            if (!selectedInbox) {
                              toast.error("Selecione a inbox");
                              return;
                            }
                            associateAgentBotMutation.mutate({ inboxId: selectedInbox, agentBotId: selectedAgentBot && selectedAgentBot > 0 ? selectedAgentBot : null });
                          }}
                        >
                          Salvar associação
                        </Button>
                      </div>
                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Inbox</TableHead>
                              <TableHead>Bot</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(inboxes || []).map((i) => (
                              <TableRow key={i.id}>
                                <TableCell>{`${i.id} · ${i.name}`}</TableCell>
                                <TableCell>—</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
        </TabsContent>
        <TabsContent value="bots">
              <Card>
                <CardHeader>
                  <CardTitle>Bots</CardTitle>
                  <CardDescription>Atendentes IA e associações</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!cfg?.token || !cfg?.accountId ? (
                    <Badge variant="destructive">Token ou Account ID ausente</Badge>
                  ) : (
                    <>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <label className="text-sm">Inbox</label>
                          <Select onValueChange={(v) => setSelectedInbox(Number(v))}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a inbox" />
                            </SelectTrigger>
                            <SelectContent>
                              {(inboxes || []).map((i) => (
                                <SelectItem key={i.id} value={String(i.id)}>{`${i.id} · ${i.name}`}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm">AgentBot</label>
                          <Select onValueChange={(v) => setSelectedAgentBot(Number(v))}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o bot" />
                            </SelectTrigger>
                            <SelectContent>
                              {(accountAgentBots || []).map((b) => (
                                <SelectItem key={b.id} value={String(b.id)}>{`${b.id} · ${b.name}`}</SelectItem>
                              ))}
                              <SelectItem value="0">Remover</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            if (!selectedInbox) {
                              toast.error("Selecione a inbox");
                              return;
                            }
                            associateAgentBotMutation.mutate({ inboxId: selectedInbox, agentBotId: selectedAgentBot && selectedAgentBot > 0 ? selectedAgentBot : null });
                          }}
                        >
                          Salvar associação
                        </Button>
                      </div>
                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Inbox</TableHead>
                              <TableHead>Bot</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(inboxes || []).map((i) => (
                              <TableRow key={i.id}>
                                <TableCell>{`${i.id} · ${i.name}`}</TableCell>
                                <TableCell>—</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export { ensureArray, filterByAccountId, computeStats, isValidUrl, isValidEmail, isStrongPassword, formatDuration, displayRole, displayAvailability };
const AVAILABLE_WEBHOOK_SUBS = [
  "conversation_created",
  "conversation_updated",
  "conversation_status_changed",
  "message_created",
  "message_updated",
  "conversation_typing_on",
  "conversation_typing_off",
];
